/**
 * Workflow utility helpers.
 *
 * Pure presentation helpers retained from the legacy workflow module
 * because several still-live pages (VendorHome, VendorOrders, InvoicesList,
 * PortalConsumerProfile) depend on them for status pills and formatters.
 *
 * The legacy workflow ENGINE is gone (replaced by /app/backend/workflow_engine/);
 * these are display-layer utilities only.
 */
import { Badge } from "@/components/ui/badge";

export const STATUS_PALETTE = {
  pending: { bg: "bg-slate-100", text: "text-slate-700", label: "Pending" },
  in_progress: { bg: "bg-blue-100", text: "text-blue-700", label: "In progress" },
  awaiting_review: { bg: "bg-amber-100", text: "text-amber-800", label: "Awaiting review" },
  completed: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Completed" },
  cancelled: { bg: "bg-rose-100", text: "text-rose-700", label: "Cancelled" },
  paid: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Paid" },
  unpaid: { bg: "bg-amber-100", text: "text-amber-800", label: "Unpaid" },
  overdue: { bg: "bg-rose-100", text: "text-rose-700", label: "Overdue" },
  draft: { bg: "bg-slate-100", text: "text-slate-600", label: "Draft" },
  scheduled: { bg: "bg-violet-100", text: "text-violet-700", label: "Scheduled" },
  live: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Live" },
  archived: { bg: "bg-slate-100", text: "text-slate-500", label: "Archived" },
};

function _paletteFor(status) {
  const key = (status || "").toLowerCase();
  return STATUS_PALETTE[key] || { bg: "bg-slate-100", text: "text-slate-700", label: status || "—" };
}

export function StatusPill({ status, className = "" }) {
  const p = _paletteFor(status);
  return (
    <Badge className={`${p.bg} ${p.text} hover:${p.bg} ${className}`}>
      {p.label}
    </Badge>
  );
}

export function InvoiceStatusPill({ status, className = "" }) {
  // Same component, kept as a separate export for callsite clarity.
  return <StatusPill status={status} className={className} />;
}

export function fmtMoney(amountCents, currency = "USD") {
  if (amountCents == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amountCents / 100);
  } catch {
    return `$${(amountCents / 100).toFixed(0)}`;
  }
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

export function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMs = d.getTime() - Date.now();
    const absMins = Math.abs(diffMs) / 60000;
    if (absMins < 1) return diffMs < 0 ? "just now" : "in a moment";
    if (absMins < 60) {
      const mins = Math.round(absMins);
      return diffMs < 0 ? `${mins}m ago` : `in ${mins}m`;
    }
    const absHours = absMins / 60;
    if (absHours < 24) {
      const h = Math.round(absHours);
      return diffMs < 0 ? `${h}h ago` : `in ${h}h`;
    }
    const absDays = absHours / 24;
    if (absDays < 30) {
      const days = Math.round(absDays);
      return diffMs < 0 ? `${days}d ago` : `in ${days}d`;
    }
    return fmtDate(iso);
  } catch {
    return "—";
  }
}
